import React from 'react';
import {Navigate} from 'react-router-dom';
import Select from 'react-select';

import Header from 'src/components/Header';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext'; // added import
import {useCurrentOrganization} from 'src/hooks/useAuth';
import useOrganizationUsersQuery from 'src/hooks/useOrganizationUsers';
import {ApiSignupToken, ApiUserProfile, inviteUser} from 'src/lib/api/auth';
import {
  SIGNUP_TOKEN_VALIDITY_DAYS,
  USER_ROLE_ADMIN,
  USER_ROLE_OWNER,
  USER_ROLE_READONLY,
  USER_ROLE_REGULAR,
} from 'src/lib/constants';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';
import {Locale} from 'src/lib/translations';

import RoleDescription from './RoleDescription';

type Props = {
  currentUser: ApiUserProfile;
};

const MIN_EMAIL_LENGTH = 4;

export function getLastValidTokenLocaleDateString(
  tokens: ApiSignupToken[],
  locale: Locale
): string | null {
  const currentToken = tokens[0];
  if (!currentToken) return null;
  const tokenCreationDate = new Date(currentToken.createdAt);
  const expirationDate = new Date(tokenCreationDate);
  expirationDate.setDate(tokenCreationDate.getDate() + SIGNUP_TOKEN_VALIDITY_DAYS);
  return expirationDate.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}

export default function Invite({currentUser}: Props) {
  const {t} = useTranslations();
  const currentOrg = useCurrentOrganization()!;
  const {data: orgUsers, isLoading: isLoadingOrgUsers} = useOrganizationUsersQuery(currentOrg.id);
  const [selectedRoleId, setSelectedRoleId] =
    React.useState<ApiUserProfile['role']>(USER_ROLE_READONLY);
  const [email, setEmail] = React.useState<string>('');
  const [isInvitingUser, setIsInvitingUser] = React.useState<boolean>(false);

  // Error and success messages
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [submissionError, setSubmissionError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  // TODO(new-owner): add admin role, and update owner permissions once migration is complete
  const roleOptions: {label: string; value: ApiUserProfile['role']}[] =
    currentUser.role === USER_ROLE_OWNER || currentUser.role === USER_ROLE_ADMIN
      ? [
          {label: t('adminRole'), value: USER_ROLE_ADMIN},
          {label: t('regularRole'), value: USER_ROLE_REGULAR},
          {label: t('readonlyRole'), value: USER_ROLE_READONLY},
        ]
      : [
          {label: t('regularRole'), value: USER_ROLE_REGULAR},
          {label: t('readonlyRole'), value: USER_ROLE_READONLY},
        ];

  const makeRoleOption = (role: ApiUserProfile['role']) => {
    return roleOptions.find((option) => option.value === role);
  };

  const validateEmail = (e?: React.ChangeEvent<HTMLInputElement>) => {
    if (e) e.preventDefault();

    const emailToValidate = e?.target?.value ?? '';
    if (emailToValidate.length === 0) {
      return false;
    }

    const trimmedEmail = emailToValidate.trim().toLowerCase();

    if (isLoadingOrgUsers) {
      return false;
    }

    if (orgUsers!.some((user) => user.email.toLowerCase() === trimmedEmail)) {
      setEmailError(t('emailAlreadyInOrganization', {email: trimmedEmail}));
      return false;
    }

    // Regex for email validation, in human terms:
    // - At least 1 character, including letters, numbers, underscores, hyphens, periods, and plus signs.
    // - then an @ symbol
    // - then at least 1 character, including letters, numbers, underscores, and hyphens
    // - then one or more groups of (a dot followed by 2-20 letters/numbers/hyphens)
    //   This supports multi-level TLDs like .co.nz, .govt.nz, .com.au
    // Note that this is less strict than the API's email validation, which will happen upon submission.
    const validEmailRegex = /^[\w.+-]+@[\w-]+(\.[\w-]{2,20})+$/g;
    if (trimmedEmail.match(validEmailRegex)) {
      setEmailError(null);
      return true;
    } else {
      setEmailError(t('invalidEmailFormat'));
      return false;
    }
  };

  const submitInviteUser = async () => {
    const trimmedEmail = email.trim().toLowerCase();

    setIsInvitingUser(true);
    setSubmissionError(null);
    setSuccessMessage(null);

    try {
      await inviteUser(currentOrg.id, trimmedEmail, selectedRoleId);
      setEmail('');
      setSuccessMessage(t('inviteSent', {email: trimmedEmail}));
    } catch (error) {
      // The API has more robust email validation.
      // If it returns an invalid email error that we didn't catch, still set the email-specific error
      if (error instanceof Error && error.message.includes('Invalid email')) {
        setEmailError(t('invalidEmailFormat'));
      } else {
        setSubmissionError(t('errorInvitingUser', {email: trimmedEmail}));
      }
    } finally {
      setIsInvitingUser(false);
    }
  };

  if (currentUser.role === 'readonly') {
    return <Navigate to={`/${makeUuidPrefix(currentOrg.id)}/settings/users`} replace />;
  }

  return (
    <>
      <Header />
      <div className="container mx-auto">
        <PageTitle
          pageStack={[
            {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
            {title: t('settings'), to: '/:currentOrgIdPrefix/settings'},
            {title: t('team'), to: '/:currentOrgIdPrefix/settings/users'},
            {title: t('invite'), to: '/:currentOrgIdPrefix/settings/invite'},
          ]}
        />
        <div>
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('inviteUser')}</h3>
            <div className="mt-1 text-sm text-gray-600">
              <div>{t('inviteInfo', {days: SIGNUP_TOKEN_VALIDITY_DAYS.toString()})}</div>
              <div className="mt-2 leading-6">
                <form onSubmit={(e) => e.preventDefault()} className="mb-1" noValidate>
                  <div className="flex flex-wrap gap-4">
                    <div className="w-50 ">
                      <label
                        htmlFor="role"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        {t('role')}
                      </label>
                      <div className="w-48">
                        <Select
                          options={roleOptions}
                          name="role"
                          value={makeRoleOption(selectedRoleId)}
                          onChange={(selectedOption) => {
                            const selectedRole = selectedOption!.value as ApiUserProfile['role'];
                            setSelectedRoleId(selectedRole);
                          }}
                          isDisabled={false}
                        />
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <label
                        htmlFor="emailToInvite"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        {t('emailAddress')}
                      </label>
                      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md">
                        <div className="mt-2"></div>
                        <input
                          id="emailToInvite"
                          type="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(() => {
                              e.target.value.length > MIN_EMAIL_LENGTH && validateEmail(e);
                              return e.target.value;
                            });
                            setEmailError(null);
                            setSubmissionError(null);
                            setSuccessMessage(null);
                          }}
                          className="flex-grow min-w-[120px] outline-none border-none"
                          placeholder={t('enterEmail')}
                        />
                      </div>
                    </div>
                  </div>
                </form>
                {emailError && <p className="text-xs text-red-500">{emailError}</p>}
              </div>
              <div className="flex items-center gap-4 pt-4">
                <button
                  type="button"
                  className={tw.buttonPrimary}
                  disabled={
                    isLoadingOrgUsers ||
                    email.length <= MIN_EMAIL_LENGTH ||
                    !!emailError ||
                    isInvitingUser
                  }
                  onClick={submitInviteUser}
                >
                  {isInvitingUser ? t('loading') : t('submit')}
                </button>
                {submissionError && <p className="text-xs text-red-500">{submissionError}</p>}
                {successMessage && <p className="text-xs text-blue-500">{successMessage}</p>}
              </div>
            </div>
            <div>
              <br />
            </div>
          </div>
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('RoleDescriptions')}</h3>
            <div className="mt-1 text-sm text-gray-600">
              <RoleDescription />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
